import { Group, Select, TextInput } from '@mantine/core';
import type { CompanySuggestion } from '@expeditor/shared';

/** Типы лица стороны (для электронного титула ИдСв). Совпадает с ClientType. */
export const PARTY_TYPE_OPTIONS = [
  { value: 'LEGAL_RU', label: 'Юр. лицо (РФ)' },
  { value: 'ENTREPRENEUR', label: 'ИП' },
  { value: 'INDIVIDUAL', label: 'Физлицо' },
  { value: 'LEGAL_FOREIGN', label: 'Иностранное лицо' },
];

export function partyTypeLabel(t: string | null): string {
  return PARTY_TYPE_OPTIONS.find((o) => o.value === t)?.label ?? '—';
}

/**
 * Подставить реквизиты из DaData-подсказки в поля стороны. Сеттеры name/country/
 * address опциональны — передаются там, где эти поля есть (форма поручения,
 * карточка контрагента).
 */
export function applyCompanySuggestion(
  s: CompanySuggestion,
  set: {
    setName?: (v: string) => void;
    setCountry?: (v: string) => void;
    setAddress?: (v: string) => void;
    setType: (v: string) => void;
    setInn: (v: string) => void;
    setKpp: (v: string) => void;
    setOgrn: (v: string) => void;
  },
) {
  set.setType(s.type);
  set.setInn(s.inn ?? '');
  set.setKpp(s.kpp ?? '');
  set.setOgrn(s.ogrn ?? '');
  set.setName?.(s.shortName || s.fullName);
  set.setAddress?.(s.legalAddress ?? '');
  // Для РФ-лиц страна очевидна; для иностранного — оставляем как есть.
  if (s.type !== 'LEGAL_FOREIGN') set.setCountry?.('Россия');
}

/**
 * Поля реквизитов стороны (грузоотправитель/грузополучатель/контрагент) для
 * электронного титула (ИдСв). Состав полей зависит от выбранного типа:
 * - ЮЛ РФ → ИНН, КПП, ОГРН
 * - ИП → ИНН, ОГРНИП
 * - Физлицо → ИНН
 * - Иностранное лицо → налоговый номер (Tax ID), регистрационный номер
 *
 * Один и тот же компонент используется в форме поручения и в карточке
 * контрагента — поведение идентичное. DaData-поиск (CompanyLookup) рендерится
 * отдельно в родителе — выше строки наименования.
 */
export function PartyRequisitesFields({
  type,
  setType,
  inn,
  setInn,
  kpp,
  setKpp,
  ogrn,
  setOgrn,
}: {
  type: string;
  setType: (v: string) => void;
  inn: string;
  setInn: (v: string) => void;
  kpp: string;
  setKpp: (v: string) => void;
  ogrn: string;
  setOgrn: (v: string) => void;
}) {
  const isForeign = type === 'LEGAL_FOREIGN';
  const innLabel = isForeign ? 'Налоговый номер (Tax ID)' : 'ИНН';
  const ogrnLabel =
    type === 'ENTREPRENEUR' ? 'ОГРНИП' : isForeign ? 'Регистрационный номер' : 'ОГРН';
  const showKpp = type === 'LEGAL_RU';
  const showOgrn = type !== '' && type !== 'INDIVIDUAL';

  return (
    <Group grow align="flex-start">
      <Select
        label="Тип"
        placeholder="не указан"
        clearable
        value={type || null}
        onChange={(v) => setType(v ?? '')}
        data={PARTY_TYPE_OPTIONS}
      />
      {type !== '' && (
        <TextInput label={innLabel} value={inn} onChange={(e) => setInn(e.currentTarget.value)} />
      )}
      {showKpp && (
        <TextInput label="КПП" value={kpp} onChange={(e) => setKpp(e.currentTarget.value)} />
      )}
      {showOgrn && (
        <TextInput
          label={ogrnLabel}
          value={ogrn}
          onChange={(e) => setOgrn(e.currentTarget.value)}
        />
      )}
    </Group>
  );
}
